"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { isStablemintCloseMessage } from "@/lib/wallet/stablemint";
import { Icon } from "@/components/ui/Icon";

/* Stablemint's card checkout, in a frame over the page rather than a redirect
   — the whitelabel's StablemintCardDepositModal + IframeModal. The card form
   is Stablemint's own page, so nothing sensitive touches this origin; the
   frame is allowed the Payment Request API (`allow="payment"`) and sends the
   referrer's origin, which Stablemint checks. It closes on the player's close
   button or on Stablemint's own `postMessage("close")` when the checkout is
   done; the deposit itself then arrives over the realtime stream as a toast.

   Portalled to <body>: the wallet modal it opens from is transformed while it
   animates, and a fixed element inside a transformed one is placed against
   that box rather than the viewport. */

export function StablemintCardDepositModal({ url, onClose }: { url: string; onClose: () => void }) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (isStablemintCloseMessage(event.origin, event.data)) onClose();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[55] grid place-items-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-[2px]" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="StableMint Card Deposit"
        className="modal-shadow relative flex h-[min(650px,90vh)] w-full max-w-[440px] flex-col overflow-hidden rounded-2xl border border-border-input bg-elevation-1"
      >
        <div className="flex items-center justify-end p-[8px]">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close card deposit"
            className="grid h-[24px] w-[24px] place-items-center text-fg transition-colors hover:text-fg-muted"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="relative flex-1">
          {loading && (
            <div className="absolute inset-0 grid place-items-center">
              <Icon name="refresh" size={24} className="animate-spin text-fg-subtle" />
            </div>
          )}
          <iframe
            src={url}
            title="StableMint Card Deposit"
            allow="payment"
            referrerPolicy="origin"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
            onLoad={() => setLoading(false)}
            className={loading ? "invisible h-full w-full border-none" : "visible h-full w-full border-none"}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
